import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

type Language = {
  code: string;
  name: string;
  flag: string;
};

const LANGUAGES: Language[] = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'pt', name: 'Portuguese', flag: '🇧🇷' },
];

export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();

  const currentLanguage = useMemo(() => {
    const langCode = i18n.resolvedLanguage || 'en';
    return LANGUAGES.find((l) => l.code === langCode) || LANGUAGES[0];
  }, [i18n.resolvedLanguage]);

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2"
          aria-label={t('language.select')}
        >
          <Languages className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase">
            {currentLanguage?.code}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t('language.select')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LANGUAGES.map((language) => {
          const isActive = currentLanguage?.code === language.code;

          return (
            <DropdownMenuItem
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              className="flex items-center gap-3 cursor-pointer"
            >
              <span className="text-lg" role="img" aria-label={language.name}>
                {language.flag}
              </span>
              <span className="flex-1">
                {t(`language.${language.code === 'en' ? 'english' : language.code === 'es' ? 'spanish' : 'portuguese'}`)}
              </span>
              {isActive && <Check className="h-4 w-4 text-primary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
